/*
 * Due Diligence Workbench — config defaults.
 *
 * These are the fallback constants used when no published admin config
 * exists. They mirror the prototype's defaults verbatim — changing them
 * will change the math the buyer sees, so don't tweak without reason.
 *
 * The admin panel lets operators override any of these via the
 * DdwConfig shape; the public tool merges admin overrides over the
 * defaults at load time.
 */

import type {
  FlexMatchKey,
  OwnershipKey,
  SpendTierKey,
} from './types';

// ─── Calculation constants ───────────────────────────────────

export interface CalcConstants {
  hoursPerRoutineChange: number;
  workaroundHrsPerMonth: number;
  hoursPerMajorEvent: number;
  productivityDragPct: number;
  dragHoursPerMonth: number;
  dragMonths: number;
  teamMedian: number;
  hourlyCost: number;
  benchmarkLow: number;
  benchmarkHigh: number;
}

export const DEFAULT_CALC: CalcConstants = {
  hoursPerRoutineChange: 6,
  workaroundHrsPerMonth: 1,
  hoursPerMajorEvent: 100,
  productivityDragPct: 0.30,
  dragHoursPerMonth: 160,
  dragMonths: 1,
  teamMedian: 20,
  hourlyCost: 150,
  benchmarkLow: 100000,
  benchmarkHigh: 200000,
};

// ─── Projection multipliers (lens-flex-driven) ────────────────

export interface ProjectionConstants {
  expectedExpansionsByFlex: { high: number; medium: number; low: number };
  growthIntensityByFlex: { high: number; medium: number; low: number };
}

export const DEFAULT_PROJECTION: ProjectionConstants = {
  expectedExpansionsByFlex: { high: 2, medium: 1, low: 0 },
  growthIntensityByFlex: { high: 1.0, medium: 0.5, low: 0.1 },
};

// ─── Step data (Phase 1) ─────────────────────────────────────

export interface FrictionPhaseOption {
  key: 'setup' | 'execution' | 'handoffs' | 'closure';
  label: string;
  sub: string;
}
export const FRICTION_PHASES: ReadonlyArray<FrictionPhaseOption> = [
  { key: 'setup', label: 'Setup', sub: 'Before work begins — how requests arrive, how you scope and assign' },
  { key: 'execution', label: 'Execution', sub: 'The core work — drafting, reviewing, collaborating, getting approvals' },
  { key: 'handoffs', label: 'Handoffs', sub: 'Between people or systems — transitions, version control, waiting' },
  { key: 'closure', label: 'Closure', sub: 'Completion — finalizing, storing, finding things later, reporting' },
];

export interface FlexLevelOption {
  key: 'high' | 'medium' | 'low';
  label: string;
  sub: string;
}
export const FLEX_LEVELS: ReadonlyArray<FlexLevelOption> = [
  { key: 'high', label: 'High', sub: 'M&A, growth, or jurisdiction expansion likely in 24 months' },
  { key: 'medium', label: 'Medium', sub: 'Steady change — hiring, expansion, periodic reorgs' },
  { key: 'low', label: 'Low', sub: 'Stable team, narrow use case, mature industry' },
];

export const CHANGE_INVENTORY: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'turnover', label: 'Team turnover or new hires' },
  { key: 'reorg', label: 'Reorganization or scope change' },
  { key: 'ma', label: 'M&A, divestiture, or spin-off' },
  { key: 'newMarket', label: 'New market or product line' },
  { key: 'genai', label: 'GenAI tools or workflows' },
  { key: 'erpCrm', label: 'ERP / CRM upgrade or migration' },
  { key: 'regulatory', label: 'New regulatory requirements' },
  { key: 'cyber', label: 'New cybersecurity / privacy controls' },
];

export interface OwnershipType {
  key: OwnershipKey;
  short: string;
  label: string;
  sub: string;
  behaviors: string;
}
export const OWNERSHIP_TYPES: ReadonlyArray<OwnershipType> = [
  {
    key: 'founder',
    short: 'Founder',
    label: 'Founder / management owned',
    sub: 'Incentive: build long-term value',
    behaviors:
      'Stable pricing, durable roadmap. Risk: smaller team, slower feature velocity, founder transition events.',
  },
  {
    key: 'public',
    short: 'Public',
    label: 'Public company',
    sub: 'Incentive: quarterly results + reputation',
    behaviors:
      'Predictable cadence, audited financials. Risk: end-of-quarter price pressure, M&A driven by stock performance.',
  },
  {
    key: 'vc',
    short: 'VC-backed',
    label: 'VC-backed',
    sub: 'Incentive: rapid growth → exit (5–7 years)',
    behaviors:
      'Heavy product investment, aggressive feature racing. Risk: acquisition mid-contract, pivot to enterprise pricing tier.',
  },
  {
    key: 'pe',
    short: 'PE-owned',
    label: 'PE-owned',
    sub: 'Incentive: margin optimization → exit (3–5 years)',
    behaviors:
      'Predictable extraction playbook: pricing escalation, support consolidation, feature bundling, declining R&D investment.',
  },
  {
    key: 'rollup',
    short: 'PE Roll-up',
    label: 'PE roll-up (multiple acquisitions)',
    sub: 'Incentive: consolidate market → extract value',
    behaviors:
      'Same as PE-owned, plus: competitor acquisitions reducing your alternatives, forced platform migrations, escalating switching costs.',
  },
  {
    key: 'unknown',
    short: 'Unknown',
    label: 'Unknown / undisclosed',
    sub: "Red flag: ask why they won't tell you",
    behaviors:
      "No public ownership disclosure is itself a signal. Consider: who controls the company, what their time horizon is, and why they don't want you to know.",
  },
];

// ─── Phase 2 — scorecard ─────────────────────────────────────

export interface ExtractionSignal {
  key: string;
  label: string;
  /** When true, "Yes" is the good answer (e.g., founders still active). */
  invert?: boolean;
}
export const EXTRACTION_SIGNALS: ReadonlyArray<ExtractionSignal> = [
  { key: 'recentAcquisition', label: 'Acquired or had ownership change in the last 24 months?' },
  { key: 'foundersActive', label: 'Founders or original leadership still actively involved?', invert: true },
  { key: 'priceEscalation', label: 'References reported pricing escalation or feature unbundling?' },
];

export interface FlexMatchOption {
  key: FlexMatchKey;
  label: string;
  tier: number;
  hoursPerChange: number;
}
export const FLEX_MATCH_OPTIONS: ReadonlyArray<FlexMatchOption> = [
  { key: 'selfServe', label: 'Self-service config (Legal Ops can do it)', tier: 3, hoursPerChange: 4 },
  { key: 'admin', label: 'Admin training required (in-house cert)', tier: 2, hoursPerChange: 16 },
  { key: 'it', label: 'IT involvement required', tier: 1, hoursPerChange: 60 },
  { key: 'vendorPS', label: 'Vendor professional services required', tier: 0, hoursPerChange: 160 },
];

export interface ScaleOption {
  key: string;
  label: string;
  sub: string;
  penaltyMult: number;
}
export const DEFAULT_SCALE_OUT: ReadonlyArray<ScaleOption> = [
  {
    key: 'selfServe',
    label: 'Self-service',
    sub: 'No lag, no cost — Legal Ops can extend it themselves',
    penaltyMult: 0,
  },
  {
    key: 'admin',
    label: 'Certified admin',
    sub: 'Time lag, internal cost — your in-house admin does the work',
    penaltyMult: 0.4,
  },
  {
    key: 'vendorPS',
    label: 'Vendor services',
    sub: 'Time lag, external cost — vendor professional services engagement',
    penaltyMult: 1.0,
  },
];

export const DEFAULT_SCALE_UP: ReadonlyArray<ScaleOption> = [
  {
    key: 'selfServe',
    label: 'Self-service',
    sub: 'No lag, no cost — Legal Ops handles changes through point-and-click tools',
    penaltyMult: 0,
  },
  {
    key: 'admin',
    label: 'Certified admin',
    sub: 'Time lag, internal cost — admin reconfigures for new volume',
    penaltyMult: 0.4,
  },
  {
    key: 'vendorPS',
    label: 'Vendor services',
    sub: 'Time lag, external cost — vendor engagement to scale',
    penaltyMult: 1.0,
  },
  {
    key: 'migration',
    label: 'Migration required',
    sub: 'Major lag, full replacement cost — architecture forces a switch',
    penaltyMult: 2.5,
  },
];

export const CONTRACT_PROTECTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'priceCap', label: 'Annual price increase cap (3–5% or CPI-tied)' },
  { key: 'changeCtrl', label: 'Change-of-control termination right (30–180 days)' },
  { key: 'dataExport', label: 'Full data export rights in standard formats' },
  { key: 'slaCredits', label: 'Specific SLA with credits for breach' },
];

export const EMOTIONAL_FEEL: ReadonlyArray<string> = [
  'Accessible',
  'Responsive',
  'Helpful',
  'Service-oriented',
  'Team players',
  'Professional',
  'Precise',
  'Collaborative',
  'Communicative',
  'Flexible/adaptable',
  'Praise-worthy',
];

export const EMOTIONAL_AVOID: ReadonlyArray<string> = [
  'Being overworked',
  'Being burnt out',
  'Feeling disrespected by clients/mgmt',
  'Being unprepared for change',
  'Reputational harm (team or individual)',
  'Poor service delivery',
  'Avoidable mistakes',
  'Poor response times',
  'Poor quality work',
  'Needless administrivia / wasted steps',
];

export interface AdoptionOption {
  key: string;
  label: string;
  pts: number;
}
export interface AdoptionQuestion {
  key: string;
  label: string;
  options: ReadonlyArray<AdoptionOption>;
}
export const DEFAULT_ADOPTION_QUESTIONS: ReadonlyArray<AdoptionQuestion> = [
  {
    key: 'deRisk',
    label: 'Does the vendor offer a way to assess fit before full commitment?',
    options: [
      { key: 'freeTrial', label: 'Free trial or POC available', pts: 20 },
      { key: 'paidPilot', label: 'Paid pilot with defined success criteria', pts: 14 },
      { key: 'termination', label: 'Adoption-tied termination clause in contract', pts: 10 },
      { key: 'none', label: 'No way to test before signing', pts: 0 },
      { key: 'unknown', label: "Haven't asked", pts: 5 },
    ],
  },
  {
    key: 'successPlan',
    label: 'Did the vendor provide a written success plan with measurable milestones?',
    options: [
      { key: 'yes', label: 'Yes — clear and measurable', pts: 20 },
      { key: 'vague', label: 'Verbal or vague', pts: 8 },
      { key: 'no', label: 'No', pts: 0 },
      { key: 'unknown', label: "Haven't asked", pts: 5 },
    ],
  },
  {
    key: 'csmModel',
    label: 'Customer success model?',
    options: [
      { key: 'named', label: 'Named, dedicated CSM', pts: 20 },
      { key: 'pool', label: 'Pool of CSMs (no named owner)', pts: 8 },
      { key: 'self', label: 'Self-serve / community only', pts: 2 },
    ],
  },
  {
    key: 'training',
    label: 'Are training and change-management included?',
    options: [
      { key: 'yes', label: 'Yes — included in pricing', pts: 20 },
      { key: 'extra', label: 'Available — extra cost', pts: 8 },
      { key: 'none', label: 'Not offered', pts: 0 },
    ],
  },
  {
    key: 'refsAdoption',
    label: 'References confirmed >80% active user adoption at 12 months?',
    options: [
      { key: 'yes', label: 'Yes — references confirmed', pts: 20 },
      { key: 'mixed', label: "Mixed — some did, some didn't", pts: 8 },
      { key: 'no', label: 'No — adoption stalled', pts: 0 },
      { key: 'unknown', label: "Haven't asked references", pts: 5 },
    ],
  },
  {
    key: 'teamTenure',
    label: 'Has the vendor disclosed implementation team tenure?',
    options: [
      { key: 'stable', label: 'Yes — team is stable', pts: 20 },
      { key: 'turnover', label: 'Yes — recent turnover', pts: 5 },
      { key: 'no', label: "No / wouldn't say", pts: 5 },
    ],
  },
];

export interface SpendTier {
  key: SpendTierKey;
  label: string;
  sub: string;
  annual: number;
}
export const DEFAULT_SPEND_TIERS: ReadonlyArray<SpendTier> = [
  { key: 'small', label: 'Small', sub: '<$50K/yr', annual: 50000 },
  { key: 'mid', label: 'Mid', sub: '$50K–$250K/yr', annual: 175000 },
  { key: 'large', label: 'Large', sub: '$250K–$1M/yr', annual: 500000 },
  { key: 'enterprise', label: 'Enterprise', sub: '>$1M/yr', annual: 1500000 },
];

export const OUTCOME_PLACEHOLDERS: ReadonlyArray<string> = [
  'Reduce time from business request to first attorney response',
  'Reduce how often we discover critical deadlines too late to act',
  'Increase our ability to show leadership what the team actually delivers',
];

export const DEFAULT_RED_FLAG_WORDS: ReadonlyArray<string> = [
  'software',
  'platform',
  'system',
  'tool',
  'app',
  'application',
  'CLM',
  'CRM',
  'matter management',
  'e-billing',
  'ebilling',
  'AI',
  'A.I.',
  'artificial intelligence',
  'automation',
  'dashboard',
  'workflow',
  'workflows',
  'solution',
  'product',
  'vendor',
  'license',
  'SaaS',
];

export const DEFAULT_RESEARCH_QUESTIONS = {
  q1: 'What triggered this evaluation?',
  q2: "Who's on your decision team?",
  q3: "What's your timeline?",
};

// ─── Welcome / brand defaults ────────────────────────────────

export const DEFAULT_CONTENT = {
  heroBadge: 'Due Diligence Workbench',
  heroTitle: 'Make your next legal tech decision with <em>confidence</em>.',
  heroSub:
    "Build your own evaluation lens, then score the vendors you're considering against it. Vendor-agnostic by design — you bring the vendors, the framework scores every one the same way against your criteria. No recommended product, no preferred vendor, no thumb on the scale.",
  ctaPrimary: 'Build your evaluation lens',
  navMeta: 'Due Diligence Workbench',
};

// ─── Aggregate config shape (matches admin storage) ──────────

export interface DdwConfig {
  vars?: Record<string, string>;
  content: typeof DEFAULT_CONTENT;
  redFlagWords: string[];
  calc: CalcConstants;
  projection: ProjectionConstants;
  scaleOut: ScaleOption[];
  scaleUp: ScaleOption[];
  adoptionQuestions: AdoptionQuestion[];
  spendTiers: SpendTier[];
  research: { q1: string; q2: string; q3: string };
}

export const DEFAULT_DDW_CONFIG: DdwConfig = {
  content: { ...DEFAULT_CONTENT },
  redFlagWords: [...DEFAULT_RED_FLAG_WORDS],
  calc: { ...DEFAULT_CALC },
  projection: {
    expectedExpansionsByFlex: { ...DEFAULT_PROJECTION.expectedExpansionsByFlex },
    growthIntensityByFlex: { ...DEFAULT_PROJECTION.growthIntensityByFlex },
  },
  scaleOut: DEFAULT_SCALE_OUT.map((o) => ({ ...o })),
  scaleUp: DEFAULT_SCALE_UP.map((o) => ({ ...o })),
  adoptionQuestions: DEFAULT_ADOPTION_QUESTIONS.map((q) => ({
    ...q,
    options: q.options.map((o) => ({ ...o })),
  })),
  spendTiers: DEFAULT_SPEND_TIERS.map((t) => ({ ...t })),
  research: { ...DEFAULT_RESEARCH_QUESTIONS },
};
